import { intl } from '../../../../lib';
import type { LanguageProperty } from '../../../generated/LanguageProperty';

export type CheckboxOption = {
  selectLabel: string;
  selectValue: string;
};

export const OTHER_LABEL = 'common.other';
export const OTHER_LABEL_TRANSLATED = intl(
  OTHER_LABEL as LanguageProperty,
  OTHER_LABEL
);

export interface ExtendedCheckboxOption extends CheckboxOption {
  selected: boolean;
}

/**
 * @param classNames a string separated by spaces that will be added as classNames
 */
export type EhsFormioCheckboxGroupSettings = {
  classNames?: string;
  selectOptions: CheckboxOption[];
  groupData: CheckboxOption[];
};

export type EhsFormioCheckboxGroupConstructor = {
  classNames: string;
  // the Checkbox options
  selectOptions: CheckboxOption[];
  // default values passed via a Data Grid
  groupData: CheckboxOption[];
};

export function findSelectOption(
  searchedSelectValue: string,
  translatedItems: ExtendedCheckboxOption[]
): CheckboxOption | undefined {
  const foundOption = translatedItems.find(
    option => option.selectValue === searchedSelectValue
  );

  if (!foundOption) {
    return undefined;
  }

  return {
    selectLabel: foundOption.selectLabel,
    selectValue: foundOption.selectValue
  };
}

export function isArrayOfStrings(
  value?: string | string[] | CheckboxOption[] | null
): value is string[] {
  if (!value || typeof value === 'string') {
    return false;
  }

  return (
    Array.isArray(value) &&
    (value as unknown[]).every(val => typeof val === 'string')
  );
}

/**
 * Function to check if formio is passing values as CheckboxOption items.
 *
 * @param value - the value passed from formio
 * @returns whether the value is an array of CheckboxOptions or something else
 */
export function isArrayOfCheckboxOptions(
  value?: string | string[] | CheckboxOption[] | null
): value is CheckboxOption[] {
  if (
    !Array.isArray(value) ||
    value.some((val: string | CheckboxOption) => typeof val === 'string')
  ) {
    return false;
  }

  return (value as CheckboxOption[]).every(
    (val: CheckboxOption) =>
      typeof val === 'object' && val !== null && 'selectValue' in val
  );
}

export function transformDefaultValuesToCheckboxOptions(
  defaultValue: string[],
  translatedItems: ExtendedCheckboxOption[],
  canHaveOther: boolean = false
): CheckboxOption[] {
  const defaultCheckboxGroupValue: CheckboxOption[] = [];
  for (const value of defaultValue) {
    const match = translatedItems.find(option => option.selectValue === value);
    if (match) {
      defaultCheckboxGroupValue.push({
        selectLabel: match.selectLabel,
        selectValue: match.selectValue
      });
      continue;
    }

    if (canHaveOther) {
      defaultCheckboxGroupValue.push({
        selectLabel: OTHER_LABEL,
        selectValue: value
      });
    }
  }
  return defaultCheckboxGroupValue;
}

export function updateCheckboxGroupValue(
  value: CheckboxOption[],
  selectValue: string
): CheckboxOption[] {
  if (selectValue === OTHER_LABEL) {
    return value.filter(val => val.selectLabel !== OTHER_LABEL);
  }

  return value.filter(val => val.selectValue !== selectValue);
}

/**
 * Function to filter other CheckboxOption by its label
 * since its value is not unique.
 *
 * @param checkbox - the CheckboxOption to
 * @param isFilter - whether the filter is active
 * @param otherLabel - the label to filter against (this label will be excluded)
 * @returns whether the label matches the other label
 */
export function filterOther(
  checkbox: CheckboxOption,
  isFilter: boolean,
  otherLabel: string
): boolean {
  if (isFilter) {
    return checkbox.selectLabel !== otherLabel;
  }
  return true;
}

export function createDefaultValueForOther(
  selectOptions: CheckboxOption[],
  defaultValue?: string[]
) {
  // find common.other
  const other = selectOptions.find(
    option => option.selectLabel === OTHER_LABEL
  );

  if (!other) {
    return '';
  }

  if (other.selectValue && defaultValue?.includes(other.selectValue)) {
    return other.selectValue;
  }

  return '';
}
